const { getElement, getElements, formatMoney } = window.BAO.Utils

/**
 * @typedef {Object} SelectedVariantOption
 *
 * @property {string} label
 * @property {string} value
 */

const selectors = {
  addToCart: '[data-product-add-to-cart]',
  addToCartText: '[data-product-add-to-cart-text]',
  backInStockTrigger: '[data-product-bis-trigger]',
  form: '[data-product-form]',
  loyalty: '[data-product-loyalty]',
  price: '[data-product-price]',
  sku: '[data-product-sku]',
  variantIndicator: '[data-product-variant-indicator]',
}

class Variant {
  constructor (data) {
    /**
     * @type {ShopifyVariant}
     * @public
     */
    this.data = data
  }

  async setPrice (context) {
    await window.BAO.sectionRenderer
      .fetch(
        'product-price',
        `${context.dataset.url}?variant=${this.data.id}&section_id=product-price`,
      )
      .then((res) => {
        const newDOM = window.BAO.sectionRenderer.convertToDOM(
          res['product-price'],
        )
        const additionalCosts = window.BAO.additionalCosts
        const myself = newDOM.querySelector('.shopify-section')
        const priceEl = myself.querySelector('[data-price]')

        priceEl.innerHTML = formatMoney(
          parseInt(priceEl.dataset.price) + additionalCosts,
        )

        const priceElements = getElements(
          `${selectors.price}[data-form-id='${context.dataset.formId}']`,
        )
        priceElements.forEach((el) => {
          el.innerHTML = myself.innerHTML
        })

        window.BAO.dispatchEvent(window.BAO.EVENTS.PDP.PRICE_CHANGE)
      })
  }

  async setLoyalty (context) {
    const loyaltyElements = getElements(
      `${selectors.loyalty}[data-form-id='${context.dataset.formId}']`,
    )

    if (loyaltyElements.length === 0) return

    await window.BAO.sectionRenderer
      .fetch(
        'product-price-loyalty',
        `${context.dataset.url}?variant=${this.data.id}&section_id=product-price-loyalty`,
      )
      .then((res) => {
        const newDOM = window.BAO.sectionRenderer.convertToDOM(
          res['product-price-loyalty'],
        )
        const myself = newDOM.querySelector('.shopify-section')

        loyaltyElements.forEach((el) => {
          el.innerHTML = myself.innerHTML
        })
      })

    window.loyaltylion.ui?.refresh()
  }

  setAvailability (context) {
    getElements(
      `${selectors.backInStockTrigger}[data-form-id='${context.dataset.formId}']`,
    ).forEach((el) => {
      el.setAttribute('aria-hidden', this.data.available)
      el.setAttribute(
        'data-product-url',
        `${context.dataset.url}?variant=${this.data.id}`,
      )
    })

    getElements(
      `${selectors.addToCart}[data-form-id='${context.dataset.formId}']`,
    ).forEach((el) => {
      el.disabled = !this.data.available
      el.setAttribute('aria-hidden', !this.data.available)
    })

    getElements(
      `${selectors.addToCartText}[data-form-id='${context.dataset.formId}']`,
    ).forEach((el) => {
      if (el.hasAttribute('data-product-personalisation-add-to-cart-text')) {
        el.textContent = this.data.available
          ? window.theme.strings.addToCartPersonalisation
          : window.theme.strings.soldOut
      } else {
        el.textContent = this.data.available
          ? window.theme.strings.addToCart
          : window.theme.strings.soldOut
      }
    })

    getElements(
      `[data-personalisation-tabs-external-el="personalisation-button"][data-form-id='${context.dataset.formId}']`,
    ).forEach((el) => {
      if (this.data.available) {
        el.removeAttribute('data-disabled')
      } else {
        el.setAttribute('data-disabled', '')
      }
    })
  }

  updateInput (context) {
    const inputs = getElements(
      `form[action*="/cart/add"][id="${context.dataset.formId}"] [name="id"]`,
    )

    inputs.forEach((input) => {
      input.value = this.id
      input.dispatchEvent(new Event('change', { bubbles: true }))
    })
  }

  updateStockMessage () {
    const variantId = this.data.id
    const variantInventoryMessages = getElements(
      '[data-variant-inventory-message]',
    )

    if (variantInventoryMessages.length === 0) return

    variantInventoryMessages.forEach((variantInventoryMessage) => {
      const variantMessageId = parseInt(
        variantInventoryMessage.dataset.variantId,
        10,
      )

      if (variantMessageId === variantId) {
        const variantInventory = parseInt(
          variantInventoryMessage.dataset.variantInventory,
          10,
        )
        const stockLevelTrigger = parseInt(
          variantInventoryMessage.dataset.variantInventoryTrigger,
          10,
        )

        if (stockLevelTrigger <= variantInventory) {
          variantInventoryMessage.classList.add('prd-Form_Message-visible')
        } else {
          variantInventoryMessages.forEach((variantInventoryMessage) => {
            variantInventoryMessage.classList.remove(
              'prd-Form_Message-visible',
            )
          })
        }
      }
    })
  }

  updateWishlistButton () {
    const variantID = this.data.id
    const wishlistButton = getElement('[data-variant-el="wishlist-button"]')

    if (!wishlistButton) return

    wishlistButton.setAttribute('data-variant-id', variantID)

    const wishlistButtonVariantUrl =
      wishlistButton.getAttribute('data-variant-url')

    if (wishlistButtonVariantUrl) {
      const variantUrl = wishlistButtonVariantUrl
      const variantUrlSplit = variantUrl.split('?variant=')
      const newVariantUrl = `${variantUrlSplit[0]}?variant=${variantID}`

      wishlistButton.setAttribute('data-variant-url', newVariantUrl)
    }
  }

  setUrl () {
    window.history.replaceState(
      { path: this.url.toString() },
      '',
      this.url.toString(),
    )
  }

  setShareUrl () {
    const shareButtons = getElements('share-button')
    if (shareButtons.length === 0) return

    shareButtons.forEach((shareButton) => {
      shareButton.updateUrl(this.url.toString())
    })
  }

  setMedia () {
    const mediaGallery = getElement('product-media')

    if (!mediaGallery) return

    const matchedSlideIndex = getElements(
      '[data-product-media-el="slide"]',
    ).findIndex((el) => el.dataset.variantIds.includes(this.id))

    if (matchedSlideIndex < 0) return

    mediaGallery.carousel.moveToIdx(matchedSlideIndex)
  }

  /**
   * @returns {number}
   */
  get id () {
    return this.data.id
  }

  /**
   * @returns {URL}
   */
  get url () {
    // eslint-disable-next-line compat/compat
    const url = new URL(window.location.href)
    url.searchParams.set('variant', this.id.toString())

    return url
  }
}

export class VariantOptions extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['data', 'option', 'option-choice']
  }

  constructor () {
    super()

    /**
     * @type {Variant[]}
     * @public
     */
    this.collectedVariants = [new Variant(this.defaultVariant)]
  }

  setupListeners () {
    super.setupListeners()
    this.onVariantChange = this.onVariantChange.bind(this)

    this.listeners.add(this, 'change', this.onVariantChange)

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PDP.UPDATE_PRICE,
      this.onVariantChange,
    )

    this.getPreselectedVariant()
  }

  getPreselectedVariant () {
    const urlParams = window.location.search

    if (urlParams.includes('?variant=')) {
      const initialVariant = this.getVariant()
      initialVariant.updateStockMessage(this)

      if (this.personalisation) {
        window.BAO.dispatchEvent(window.BAO.EVENTS.PDP.VARIANT_CHANGED, {
          variant: initialVariant,
        })
      }
    }
  }

  onVariantChange (e) {
    const variant = this.getVariant()

    this.updateIndicator(e.target.name, e.target.dataset.value, this)

    if (this.updateUrl) {
      setTimeout(() => {
        document.querySelector('site-drawers')?.closeDrawer()
      }, 300)
    }

    if (!variant?.data) {
      this.throwError(
        `Variant not found for selected options: ${JSON.stringify(
          this.selectedOptions,
        )}`,
      )

      return
    }

    variant.setAvailability(this)
    variant.setPrice(this)
    variant.setLoyalty(this)
    variant.updateInput(this)
    variant.updateStockMessage(this)
    variant.updateWishlistButton(this)

    // Retrieve site-drawers custom element and close drawers

    if (this.updateUrl) {
      if (!this.currentVariant) {
        this.resetPage(variant)

        return
      }

      variant.setUrl()
      variant.setShareUrl()
    }

    variant.setMedia()

    if (this.personalisation) {
      window.BAO.dispatchEvent(window.BAO.EVENTS.PDP.VARIANT_CHANGED, {
        variant,
      })
    }
  }

  updateIndicator (name, value, context) {
    const variantIndicators = getElements(
      `${selectors.variantIndicator}[data-form-id='${context.dataset.formId}']`,
    )
    const variantIndicator = variantIndicators.find(
      (el) => el.dataset.name === name,
    )

    if (!variantIndicator) return

    variantIndicator.textContent = value
    variantIndicator.classList.remove('prd-Form_VariantText-light')

    // Update external indicators
    const externalVariantIndicators = document.querySelectorAll(
      '[data-variant-external-el="variantIndicator"]',
    )
    externalVariantIndicators.forEach((element) => {
      if (element.dataset.name === name) {
        element.textContent = value
        element.classList.remove('prd-Form_VariantText-light')
      }
    })

    const sizeTitle = this.externalEls['size-title']?.element
    if (!sizeTitle) return

    sizeTitle.textContent = value.split('(')[0]
  }

  /**
   * @param {Variant} variant
   *
   * @returns {void}
   */
  resetPage (variant) {
    window.history.replaceState(
      { path: window.location.pathname },
      '',
      window.location.pathname,
    )
  }

  /**
   * @param {ShopifyVariant} data
   *
   * @returns {Variant}
   */
  createVariant (data) {
    const variant = new Variant(data)
    this.collectedVariants.push(variant)

    return variant
  }

  /**
   * @returns {Variant}
   */
  getVariant () {
    if (!this.currentVariant) {
      return
    }

    const existingVariant = this.collectedVariants.find(
      (variant) => variant.id === this?.currentVariant?.id,
    )
    if (existingVariant) return existingVariant

    return this.createVariant(this.currentVariant)
  }

  /**
   * @returns {SelectedVariantOption[]}
   */
  get selectedOptions () {
    this.throwError(`selectedOptions needs to be implemented on this class`)

    return []
  }

  get formId () {
    return this.dataset.formId
  }

  get updateUrl () {
    return this.dataset.updateUrl === 'true'
  }

  get personalisation () {
    return this.dataset.personalisation === 'true'
  }

  /**
   * @returns {ShopifyVariant}
   */
  get defaultVariant () {
    return this.data[0]
  }

  /**
   * @returns {ShopifyVariant|undefined}
   */
  get currentVariant () {
    return this.data.find((variant) => {
      return variant.options.every(
        (option, index) => this.selectedOptions[index].value === option,
      )
    })
  }

  /**
   * @returns {ShopifyVariant[]}
   */
  get data () {
    return JSON.parse(this.els.data.element.innerText)
  }
}

export class VariantRadios extends VariantOptions {
  get selectedOptions () {
    return this.els.option.elements.map((el) => {
      const { name } = el.dataset
      const choice = getElements(this.els['option-choice'].selector, {
        context: el,
      }).find((el) => el.checked)?.value

      return {
        label: name,
        value: choice,
      }
    })
  }
}

export class VariantSelects extends VariantOptions {
  static get requiredElements () {
    return ['data', 'option']
  }

  get selectedOptions () {
    return this.els.option.elements.map((el) => {
      const { name } = el.dataset

      return {
        label: name,
        value: el.querySelector('select').value,
      }
    })
  }
}

export class QuickBuyVariantSelects extends VariantOptions {
  static get requiredElements () {
    return ['data']
  }

  get selectedOptions () {
    if (!this.els.option?.elements) {
      return []
    }

    return this.els.option.elements.map((el) => {
      const { name } = el.dataset

      return {
        label: name,
        value: el.querySelector('select').value,
      }
    })
  }

  onVariantChange (e) {
    const variant = this.getVariant()

    this.updateIndicator(e.target.name, e.target.dataset.value, this)

    if (!variant?.data) {
      this.throwError(
        `Variant not found for selected options: ${JSON.stringify(
          this.selectedOptions,
        )}`,
      )

      return
    }

    variant.setAvailability(this)
    variant.setPrice(this)
    variant.setLoyalty(this)
    variant.updateInput(this)
    variant.updateStockMessage(this)
    this.updateKlarnaPlacement(variant)

    if (this.personalisation) {
      window.BAO.dispatchEvent(window.BAO.EVENTS.PDP.VARIANT_CHANGED, {
        variant,
      })
    }
  }

  updateKlarnaPlacement (variant) {
    const klarnaPlacement = getElement(
      `klarna-placement[data-form-id='${this.dataset.formId}']`,
    )

    if (!klarnaPlacement) return

    klarnaPlacement.setAttribute('data-purchase-amount', variant.data.price)
    klarnaPlacement.refresh?.()
  }
}

if (!customElements.get('variant-radios')) {
  customElements.define('variant-radios', VariantRadios)
}

if (!customElements.get('variant-selects')) {
  customElements.define('variant-selects', VariantSelects)
}

if (!customElements.get('quickbuy-variant-selects')) {
  customElements.define('quickbuy-variant-selects', QuickBuyVariantSelects)
}
